import React, { useCallback, useMemo, useReducer } from "react";
import { useTranslations } from "@adsk/acc-issues-translations";
import { DocumentTextIcon, SheetsIcon } from "@adsk/alloy-react-icon";
import styled from "styled-components";
import {
  PlacementEntityType,
  PlacementFile,
  PlacementSheet,
  PlacementFlowStatus,
} from "./types";
import { useDropdownPlugins } from "./hooks/useDropdownPlugins";
import { DropdownOption } from "@adsk/acc-issues-common/types/dropdown";

interface ReducerState {
  status: PlacementFlowStatus;
  entityType?: PlacementEntityType;
  entity?: PlacementFile | PlacementSheet;
}

const ACTION_TYPES = {
  SELECT_ENTITY_TYPE: "SELECT_ENTITY_TYPE",
  SELECT_ENTITY: "SELECT_ENTITY",
  ASSOCIATE_ENTITY: "ASSOCIATE_ENTITY",
  RESET_TO_INITIAL: "RESET_TO_INITIAL",
} as const;

const selectEntityTypeAction = (entityType: PlacementEntityType) => ({
  type: ACTION_TYPES.SELECT_ENTITY_TYPE,
  entityType,
});

const selectEntityAction = (entity: PlacementFile | PlacementSheet) => ({
  type: ACTION_TYPES.SELECT_ENTITY,
  entity,
});

const associateEntityAction = () => ({ type: ACTION_TYPES.ASSOCIATE_ENTITY });

const resetToInitialAction = () =>
  ({ type: ACTION_TYPES.RESET_TO_INITIAL } as const);

type ReducerAction =
  | ReturnType<typeof selectEntityTypeAction>
  | ReturnType<typeof selectEntityAction>
  | ReturnType<typeof associateEntityAction>
  | ReturnType<typeof resetToInitialAction>;

const initialState = { status: PlacementFlowStatus.NOTHING_SELECTED };

const reducer = (state: ReducerState, action: ReducerAction): ReducerState => {
  switch (action.type) {
    case ACTION_TYPES.SELECT_ENTITY_TYPE:
      return {
        ...state,
        status: PlacementFlowStatus.ENTITY_TYPE_SELECTED,
        entityType: action.entityType,
      };
    case ACTION_TYPES.SELECT_ENTITY:
      return {
        ...state,
        status: PlacementFlowStatus.ENTITY_SELECTED,
        // @ts-ignore
        entity: action.entity,
      };
    case ACTION_TYPES.ASSOCIATE_ENTITY:
      return { ...state, status: PlacementFlowStatus.ENTITY_ASSOCIATED };
    case ACTION_TYPES.RESET_TO_INITIAL:
      return initialState;
    default:
      return state;
  }
};

const OPTIONS_ORDER = [PlacementEntityType.FILES, PlacementEntityType.SHEETS];

const OptionContainer = styled.div`
  display: flex;
  gap: 8px;
`;

export const usePlacementFlow = () => {
  const [state, dispatch] = useReducer(reducer, initialState);

  const selectEntityType = useCallback(
    (entityType: PlacementEntityType) =>
      dispatch(selectEntityTypeAction(entityType)),
    []
  );
  const selectEntity = useCallback(
    (entity: PlacementFile | PlacementSheet) =>
      dispatch(selectEntityAction(entity)),
    []
  );

  const associateEntity = useCallback(() => {
    if (window.location.hash) {
      window.history.back();
    }
    dispatch(associateEntityAction());
  }, []);
  const resetToInitial = useCallback(() => {
    if (window.location.hash) {
      window.history.back();
    }
    return dispatch(resetToInitialAction());
  }, []);

  const { T } = useTranslations();
  const { docsPlugin, sheetsPlugin } = useDropdownPlugins();
  const selectMessage = T.translate("actions.select");
  const optionsByEntityType = useMemo(
    () =>
      ({
        [PlacementEntityType.FILES]: {
          type: PlacementEntityType.FILES,
          plugin: {
            ...docsPlugin,
            AddRelationshipsButtonText: () => {
              return <>{selectMessage}</>;
            },
          },
          iconComponent: DocumentTextIcon,
          panelTitle: T.translate(
            "issueCreation.placement.fileSelectionPanelTitle"
          ),
          name: T.translate("issueCreation.placement.files"),
        },
        [PlacementEntityType.SHEETS]: {
          type: PlacementEntityType.SHEETS,
          plugin: {
            ...sheetsPlugin,
            AddRelationshipsButtonText: () => {
              return <>{selectMessage}</>;
            },
          },
          iconComponent: SheetsIcon,
          panelTitle: T.translate(
            "issueCreation.placement.sheetSelectionPanelTitle"
          ),
          name: T.translate("issueCreation.placement.sheets"),
        },
      } as const),
    [T, docsPlugin, sheetsPlugin, selectMessage]
  );

  const getIconForEntityType = (entityType: PlacementEntityType) =>
    optionsByEntityType[entityType].iconComponent;

  const getNameForEntityType = (entityType: PlacementEntityType) =>
    optionsByEntityType[entityType].name;

  const dropdownOptions = useMemo(
    () =>
      OPTIONS_ORDER.map(entityType => {
        const LabelComponent =
          optionsByEntityType[entityType].plugin.PluralText;
        return {
          value: entityType,
          label: <LabelComponent />,
        };
      }),
    [optionsByEntityType]
  );

  const selectedEntityTypeConfig = useMemo(
    () => state.entityType && optionsByEntityType[state.entityType],
    [optionsByEntityType, state]
  );

  const optionRenderer = useCallback(
    (option: DropdownOption<PlacementEntityType>) => {
      const IconComponent = optionsByEntityType[option.value].iconComponent;
      return (
        <OptionContainer>
          <IconComponent size={20} />
          {option.label}
        </OptionContainer>
      );
    },
    [optionsByEntityType]
  );

  return {
    selectEntityType,
    selectEntity,
    associateEntity,
    resetToInitial,
    dropdownOptions,
    optionRenderer,
    selectedEntityTypeConfig,
    entityType: state.entityType,
    entity: state.entity,
    status: state.status,
    getNameForEntityType,
    getIconForEntityType,
  };
};
